import { useState, useEffect } from 'react';
import { SEC } from './SecurityTheme';
import {
  getSecurityAlertNotificationSettings,
  saveSecurityAlertNotificationSettings,
} from '../../services/api';
import {
  Bell,
  BellOff,
  Loader2,
  Save,
  CheckCircle2,
  X,
  Plus,
} from 'lucide-react';

export default function AlertNotificationSettings() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [minSeverity, setMinSeverity] = useState<'critical' | 'warning' | 'all'>('critical');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    if (open && !loading) {
      setLoading(true);
      getSecurityAlertNotificationSettings()
        .then(resp => {
          if (resp.success && resp.settings) {
            setEnabled(resp.settings.enabled);
            setRecipients(resp.settings.recipients || []);
            setMinSeverity(resp.settings.minSeverity || 'critical');
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveSecurityAlertNotificationSettings({ enabled, recipients, minSeverity });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    const email = newEmail.trim();
    if (email && !recipients.includes(email)) {
      setRecipients([...recipients, email]);
      setNewEmail('');
    }
  };

  const removeRecipient = (email: string) => {
    setRecipients(recipients.filter(r => r !== email));
  };

  return (
    <div
      style={{
        margin: '0 24px',
        marginTop: 12,
        background: SEC.panel,
        border: `1px solid ${SEC.border}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Toggle bar */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', padding: '10px 16px',
          background: 'transparent', border: 'none',
          color: SEC.t2, cursor: 'pointer', fontSize: 12, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}
      >
        {enabled ? <Bell size={14} style={{ color: SEC.cyan }} /> : <BellOff size={14} />}
        <span>Email Notifications</span>
        {enabled && (
          <span style={{
            padding: '1px 8px', borderRadius: 9999,
            background: `${SEC.green}20`, color: SEC.green,
            fontSize: 10, fontWeight: 600,
          }}>
            ON
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: SEC.t3 }}>
          {open ? 'Close' : 'Configure'}
        </span>
      </button>

      {/* Settings panel */}
      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${SEC.border}` }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: SEC.t3 }}>
              <Loader2 size={16} className="animate-spin" style={{ margin: '0 auto 6px' }} />
              Loading settings...
            </div>
          ) : (
            <>
              {/* Enable toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0' }}>
                <button
                  onClick={() => setEnabled(!enabled)}
                  style={{
                    width: 36, height: 20, borderRadius: 10,
                    background: enabled ? SEC.cyan : SEC.panel2,
                    border: `1px solid ${enabled ? SEC.cyan : SEC.border}`,
                    cursor: 'pointer', position: 'relative', padding: 0,
                    transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#fff', position: 'absolute', top: 1,
                    left: enabled ? 17 : 1, transition: 'left 0.2s',
                  }} />
                </button>
                <span style={{ fontSize: 13, color: SEC.t1, fontWeight: 500 }}>
                  {enabled ? 'Notifications enabled' : 'Notifications disabled'}
                </span>
              </div>

              {/* Severity threshold */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: SEC.t3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Minimum Severity
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['critical', 'warning', 'all'] as const).map(sev => (
                    <button
                      key={sev}
                      onClick={() => setMinSeverity(sev)}
                      style={{
                        padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                        background: minSeverity === sev ? SEC.cyanGlow : SEC.panel2,
                        border: `1px solid ${minSeverity === sev ? SEC.cyan : SEC.border}`,
                        color: minSeverity === sev ? SEC.cyan : SEC.t2,
                        cursor: 'pointer',
                      }}
                    >
                      {sev === 'all' ? 'All Severities' : sev.charAt(0).toUpperCase() + sev.slice(1) + ' only'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recipients */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: SEC.t3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Recipients
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  {recipients.map(email => (
                    <span key={email} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 4,
                      background: SEC.panel2, border: `1px solid ${SEC.border}`,
                      fontSize: 12, fontFamily: 'monospace', color: SEC.t1,
                    }}>
                      {email}
                      <button
                        onClick={() => removeRecipient(email)}
                        style={{ background: 'none', border: 'none', color: SEC.t3, cursor: 'pointer', padding: 0 }}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addRecipient()}
                    placeholder="email@example.com"
                    style={{
                      flex: 1, padding: '6px 10px',
                      background: SEC.panel2, border: `1px solid ${SEC.border}`,
                      borderRadius: 4, color: SEC.t1, fontSize: 12, outline: 'none',
                    }}
                  />
                  <button
                    onClick={addRecipient}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '6px 10px', borderRadius: 4,
                      background: SEC.panel2, border: `1px solid ${SEC.border}`,
                      color: SEC.cyan, cursor: 'pointer', fontSize: 12,
                    }}
                  >
                    <Plus size={12} /> Add
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div style={{
                  padding: '6px 10px', borderRadius: 4, marginBottom: 8,
                  background: 'rgba(239,68,68,0.1)', color: SEC.red, fontSize: 12,
                }}>
                  {error}
                </div>
              )}

              {/* Save button */}
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 16px', borderRadius: 4,
                  background: saved ? SEC.green : SEC.cyan,
                  border: 'none', color: '#000', cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                }}
              >
                {saving ? (
                  <><Loader2 size={12} className="animate-spin" /> Saving...</>
                ) : saved ? (
                  <><CheckCircle2 size={12} /> Saved</>
                ) : (
                  <><Save size={12} /> Save Settings</>
                )}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
